'use strict';

const _ = require('underscore');
const Backbone = require('backbone');
const i18n = require('i18next');
const SyncStateModel = require('system/domain/entity/sync-state');
const NodeMetaModel = require('system/domain/entity/node-meta');
const LinkMetaModel = require('system/domain/entity/link-meta');
const VectorUtil = require('common/vector-util');

const DiagramModel = Backbone.Model.extend({
    /**
     * Diagram Model を定義する
     * (Backbone.Model を extend する)
     */
    kind: 'diagram',

    initialize: function(params) {
        _.bindAll(this, 'i18nkey', 'hasNode', 'hasLink');
        params = params ? params : {};
        let syncState = params.syncState || SyncStateModel.States.synced;
        this.syncState = new SyncStateModel({state: syncState});
        this.name = this.get('type');
        let nodeMetas = new Backbone.Collection();
        let linkMetas = new Backbone.Collection();

        _.each(this.get('records'), function(val, key){
            val.creating = false;
            val.deleting = false;
            let m = new NodeMetaModel(val);
            m.set({id: key});
            nodeMetas.add(m);
        });
        _.each(this.get('relations'), function(val, key){
            val.creating = false;
            val.deleting = false;
            let m = new LinkMetaModel(val);
            m.set({id: key});
            linkMetas.add(m);
        });

        this._nodeMetas = nodeMetas;
        this._linkMetas = linkMetas;
        this.listenTo(this._nodeMetas, 'add', function(m) {
            this.trigger('add:meta', m);
        });
        this.listenTo(this._linkMetas, 'add', function(m) {
            this.trigger('add:meta', m);
        });
        this.listenTo(this._nodeMetas, 'update', function(m) {
            this.trigger('update:meta', m);
        });
        this.listenTo(this._nodeMetas, 'change:shape', function(m) {
            this.trigger('update:meta', m);
        });
        this.listenTo(this._nodeMetas, 'change:is_zone', function(m) {
            this.trigger('update:meta', m);
        });
        this.listenTo(this._linkMetas, 'update', function(m) {
            this.trigger('update:meta', m);
        });

        // Model 詳細画面での編集ロック
        this._isEditLocked = true;
    },

    defaults: {
        name: '',
        type: 'basic',
        description: '',
        records: {},
        relations: {}
    },

    validation: {
        name: {
            required: true
        },
        type: {
            required: true
        }
    },

    idParams: function() {
        return {
            diagram_id: this.get('diagram_id'),
            group_id: this.getProjectId(),
            project_id: this.get('project_id')
        };
    },

    getProjectId: function() {
        return this.get('project_id');
    },

    i18nkey: function(attribute, fieldName) {
        fieldName = fieldName || 'name';
        return 'diagram:' + attribute + '.' + this.get(attribute) + '.' + fieldName;
    },

    // url: function() {
    //     let key = this.isNew() ? 'api:diagrams' : 'api:diagram';
    //     return Balus.url_for(key, this.idParams());
    // },

    path: function() {
        const projectId = this.getProjectId();
        return `/projects/${projectId}/diagrams/${this.id}`;
    },

    /**
     * 特定の時点での Diagram を表す URL を返す
     *
     * @param dt {Moment} - どの時点かを表す
     * @returns {string}
     */
    pathAt: function(dt) {
        return `${this.path()}?dt=${dt.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;
    },

    /**
     * 特定の時点(from と to) の Diagram の差分を表す URL を返す
     *
     * @param from {Moment} - どの時点かを表す
     * @param to {Moment} - どの時点かを表す、省略した場合は最新にになる
     * @returns {string}
     */
    pathDiff: function(from, to) {
        if(to) {
            return `${this.path()}/diff?from=${from.utc().format('YYYY-MM-DDTHH:mm:ss')}Z&to=${to.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;
        }

        return `${this.path()}/diff?from=${from.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`;
    },

    isDeleted: function() {
        return this.get('deleted');
    },

    hasElement: function(e) {
        if(e.kind == 'node') {
            return this.hasNode(e);
        } else {
            return this.hasLink(e);
        }
    },

    hasNode: function(node) {
        return this._nodeMetas.get(node.id);
    },

    hasLink: function(link) {
        return this._linkMetas.get(link.id);
    },

    hasComment: function() {
        return this.get('has_comment');
    },

    hasTask: function() {
        return this.get('has_task');
    },

    getNodeMeta: function(uid) {
        return this._nodeMetas.get(uid);
    },

    createNodeMeta: function(metaJson) {
        this._nodeMetas.add(new NodeMetaModel(metaJson));
    },

    deleteNodeMeta: function(uid) {
        this._nodeMetas.remove(uid);
    },

    getLinkMeta: function(uid) {
        return this._linkMetas.get(uid);
    },

    createLinkMeta: function(metaJson) {
        this._linkMetas.add(new LinkMetaModel(metaJson));
    },

    deleteLinkMeta: function(uid) {
        this._linkMetas.remove(uid);
    },

    /**
     * 指定した Node を全て含むような最小の Rect の大きさと中心を返す
     * nodes の length が 0 の場合 null を返す。
     *
     * @param nodes
     */
    calcRectContainNodes: function(nodes) {
        let vectors = _.chain(nodes)
            .filter(function(model) {
                return this.hasNode(model);
            }, this)
            .map(function(model) {
                let meta = this.getNodeMeta(model.id);
                let width = 256;
                let height = 256;
                if(meta.get('is_zone') && meta.get('shape').kind == 'rect') {
                    let attr = meta.get('shape').attr;
                    width = attr.width;
                    height = attr.height;
                }

                return [
                    {
                        x: meta.x - width / 2,
                        y: meta.y - height / 2
                    },
                    {
                        x: meta.x + width / 2,
                        y: meta.y - height / 2
                    },
                    {
                        x: meta.x - width / 2,
                        y: meta.y + height / 2
                    },
                    {
                        x: meta.x + width / 2,
                        y: meta.y + height / 2
                    }
                ];
            }, this)
            .flatten()
            .value();

        if(vectors.length == 0) {
            return null;
        }

        let range = VectorUtil.range(vectors);
        return {
            width: range.max.x - range.min.x,
            height: range.max.y - range.min.y,
            center: VectorUtil.interpolate(range.min, range.max, 0.5)
        };
    },

    isEditLocked: function() {
        return this._isEditLocked;
    },

    setEditLock: function(lock) {
        return this._isEditLocked = (lock === true);
    }
}, {
    types: function() {
        let diagram_types = _.map(
            i18n.t('diagram:type', { returnObjects: true }),
            function(v, k) {
                return {
                    type: k,
                    name: v.name,
                    available: v.available
                };
            }
        );

        return _.filter(
            diagram_types,
            function(v) {
                if (_.isUndefined(v.available)) {
                    return true;
                }
                if (v.available == "all") {
                    return true;
                }
                // if (_.isArray(v.available)) {
                //     return _.contains(v.available, Balus.app.app_env);
                // }
            }
        );
    }
});

module.exports = DiagramModel;



// WEBPACK FOOTER //
// ./system/domain/entity/diagram.js